/**
 * Wire shapes of a task (`tareas/{project}/search` and `tareas/{project}/store-update`), as the
 * production modal in `pages/tema` reads them. Keys are the API's own.
 */

export interface TaskConstraints {
  max_quantity_upload_files: number;
  /** In megabytes. */
  max_size_upload_files: number;
}

/**
 * A resource of a task, in either direction:
 * - `project_resources`: the reference files the teacher attached (`title`, `file`, `resource_id`).
 * - `user_resources`: what the learner has in the task. `type_resource: 'file'` is a file picked in
 *   this session and still to be sent (`upload` holds the `File`); `'media'` is one already stored.
 *   The extra keys of a stored media travel back untouched, the way production appends them.
 */
export interface TaskResource {
  resource_id?: number;
  title?: string;
  name?: string;
  /** Path of a reference file, relative to the bucket (or an absolute https url). */
  file?: string;
  size?: number;
  type_resource?: 'file' | 'media';
  /** Only for a file picked in this session: the `File` to send. */
  upload?: File;
  [key: string]: unknown;
}

export interface TaskData {
  indications: string | null;
  project_resources: TaskResource[];
  user_resources: TaskResource[];
  /** Feedback from whoever reviewed the task. Shown only when present. */
  msg_to_user: string | null;
  constraints: TaskConstraints | null;
}

/** What `tareas/{project}/store-update` answers. `type === 'success'` is the only success. */
export interface TaskSubmitResponse {
  type?: string;
  data?: {
    msg?: string;
    status_label?: string;
    status_code?: string | number;
  };
}

/** The accordion sections of the delivery, in production's order. One open at a time. */
export type TaskSection = 'indicaciones' | 'recursos' | 'documentos' | 'comentarios';

/**
 * What `app-task-delivery` keeps so that a host which destroys and recreates it (the learning path
 * does, crossing the desktop breakpoint) can hand it back: the task as the backend answered, the
 * learner's files — picked and still to be sent included — and the open section.
 *
 * `status` is the host's task state when this was saved. A draft is only valid for the same state:
 * a task that moved on (say, to `observed`) has a new payload worth reading again.
 */
export interface TaskDraft {
  task: TaskData;
  userResources: TaskResource[];
  openSection: TaskSection | null;
  status: string | null;
}

/** Emitted by `app-task-delivery` once the task is sent — the same three values production keeps. */
export interface TaskSavedEvent {
  msg: string;
  status_label: string | null;
  status_code: string | number | null;
}

/** Task states (the backend's own codes) and how the `card` variant words them. */
/** Estados de la tarea (los del backend) en los que el alumno puede adjuntar y enviar. */
export const TASK_EDITABLE_STATUSES: readonly string[] = ['pending', 'observed'];

/** A task delivered while offline, waiting for the queue: the host sets it as the task's `status`. Closed to the learner. */
export const TASK_SYNC_PENDING = 'sync_pending';

const TASK_STATUS_LABELS: Readonly<Record<string, string>> = {
  [TASK_SYNC_PENDING]: 'Pendiente de sincronizar',
  pending: 'Pendiente',
  in_review: 'En revisión',
  observed: 'Observada',
  disapproved: 'Desaprobada',
  passed: 'Aprobada',
};

/** `null` for a code this build does not know, so the chip simply is not drawn. */
export function taskStatusLabel(status: string | null): string | null {
  return status ? TASK_STATUS_LABELS[status] ?? null : null;
}

/**
 * What a file looks like in the `card` variant: the short tag on its badge, the tone of the badge and
 * a plain description. Derived from the extension, because a task resource carries no type of its own.
 * The tones are fixed colours on purpose: a file type is not a brand colour.
 */
export interface TaskFileKind {
  tag: string;
  description: string;
  /** Background of the badge — kept as a literal here so Tailwind's scan of `.ts` files finds it. */
  tone: string;
}

const FILE_KINDS: Readonly<Record<string, TaskFileKind>> = {
  doc: { tag: 'DOC', description: 'Documento Word', tone: 'bg-blue-600' },
  pdf: { tag: 'PDF', description: 'Documento PDF', tone: 'bg-red-500' },
  xls: { tag: 'XLS', description: 'Hoja de cálculo', tone: 'bg-green-600' },
  ppt: { tag: 'PPT', description: 'Presentación', tone: 'bg-orange-600' },
  img: { tag: 'IMG', description: 'Imagen', tone: 'bg-purple-600' },
  vid: { tag: 'VID', description: 'Video', tone: 'bg-indigo-600' },
};

const GENERIC_FILE_KIND: TaskFileKind = { tag: 'ARCH', description: 'Archivo', tone: 'bg-gray-700' };

const EXTENSION_TO_KIND: Readonly<Record<string, keyof typeof FILE_KINDS>> = {
  doc: 'doc', docx: 'doc',
  pdf: 'pdf',
  xls: 'xls', xlsx: 'xls',
  ppt: 'ppt', pptx: 'ppt', pptm: 'ppt', ppsm: 'ppt', ppsx: 'ppt',
  png: 'img', jpg: 'img', jpeg: 'img', gif: 'img', eps: 'img',
  mp4: 'vid', webm: 'vid', ogg: 'vid',
};

/** Always returns one of a fixed set of objects, so a template can call it on every pass. */
export function taskFileKind(resource: TaskResource): TaskFileKind {
  const source = String(resource.file || resource.name || resource.title || '');
  const extension = source.split('?')[0].split('.').pop()?.toLowerCase() ?? '';
  const kind = EXTENSION_TO_KIND[extension];
  return kind ? FILE_KINDS[kind] : GENERIC_FILE_KIND;
}

/** Human size of a file in the `card` variant: KB under a megabyte, MB above it. Empty when unknown. */
export function taskFileSize(bytes: number | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** MIME types production accepts for a task file (`tema.page.ts`'s `validateFileType`). */
export const TASK_ACCEPTED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/x-eps',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

/** Same `accept` list the production file input carries. */
export const TASK_FILE_ACCEPT =
  'image/jpeg,image/gif,image/png,application/pdf,image/x-eps, .xls,.xlsx, .doc, .docx, .ppt, .pptx, .pptm, .ppsm, .ppsx, video/*';
